// src/pages/BlockedList.tsx
import React, { useEffect, useState } from "react";
import api from "../../shared/api/axios";
import SettingsSidebar from "../sidebars/SettingsSidebar";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getAccessToken } from "../../shared/api/axios";

type BlockedItem = {
  _id: string;
  profilename: string;
  picture: string;
  isDeactivated: boolean;
  isDeleted: boolean;
  since: string;
};


const BlockedList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [items, setItems] = useState<BlockedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [unblocking, setUnblocking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get("/block-report/block/list");
      setItems(data?.items || []);
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.msg || t("blockedList.errorLoad"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUnblock = async (userId: string) => {
    if (!userId) return;
    const confirmed = window.confirm(t("blockedList.confirmUnblock"));
    if (!confirmed) return;
    try {
      setUnblocking(userId);
      await api.post(`/block-report/unblock/${userId}`, {});
      // remove from list locally
      setItems((prev) => prev.filter((i) => i._id !== userId));
    } catch (e: any) {
      console.error(e);
      alert(e?.response?.data?.msg || t("blockedList.errorUnblock"));
    } finally {
      setUnblocking(null);
    }
  };

  const handleRowClick = (id: string) => {
    // same visitor profile route as chat & likes modal
    navigate(`/visitor-profile/${id}`);
  };

  return (
    <div style={pageWrap}>
      <SettingsSidebar />

      <main style={mainStyle}>
        <div style={innerWrap}>
          <header style={headerRow}>
            <h2 style={{ margin: 0, color: "#e5e7eb" }}>{t("blockedList.title")}</h2>
            <button onClick={load} style={btn} disabled={loading}>
              {loading ? t("blockedList.refreshing") : t("blockedList.refresh")}
            </button>
          </header>

          {error && (
            <div
              style={{
                padding: 12,
                borderRadius: 8,
                background: "#7f1d1d",
                color: "#fee2e2",
                border: "1px solid #b91c1c",
                marginBottom: 12,
              }}
            >
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ padding: 16, color: "#9ca3af" }}>
              {t("blockedList.loading")}
            </div>
          ) : items.length === 0 ? (
            <div style={{ padding: 16, color: "#9ca3af" }}>
              {t("blockedList.empty")}
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {items.map((u) => (
                <div
                  key={u._id}
                  style={rowCard}
                  onClick={() => handleRowClick(u._id)}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && handleRowClick(u._id)}
                >
                  {/* Always default ghost avatar */}
                  <div style={avatarStyle}>👻</div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      <strong style={{ fontSize: 16, color: "#e5e7eb" }}>
                        {u.profilename}
                      </strong>
                      {u.isDeleted && (
                        <span
                          style={{
                            ...pill,
                            color: "#9ca3af",
                            borderColor: "#4b5563",
                            background: "#020617",
                          }}
                        >
                          {t("blockedList.statusDeleted")}
                        </span>
                      )}
                      {!u.isDeleted && u.isDeactivated && (
                        <span
                          style={{
                            ...pill,
                            color: "#fbbf24",
                            borderColor: "#f59e0b",
                            background: "#451a03",
                          }}
                        >
                          {t("blockedList.statusDeactivated")}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        color: "#9ca3af",
                        fontSize: 13,
                        marginTop: 4,
                      }}
                    >
                      {t("blockedList.blockedSince")}{" "}
                      {u.since ? new Date(u.since).toLocaleString() : "—"}
                    </div>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // don’t navigate when clicking unblock
                      handleUnblock(u._id);
                    }}
                    style={dangerBtn}
                    disabled={unblocking === u._id}
                  >
                    {unblocking === u._id
                      ? t("blockedList.unblocking")
                      : t("blockedList.unblock")}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};


const pageWrap: React.CSSProperties = {
  display: "flex",
  minHeight: "100vh",
  background: "#020617", // dark slate
  color: "#e5e7eb",
};

const mainStyle: React.CSSProperties = {
  // assuming SettingsSidebar is ~260px wide; 20px gutter
  marginLeft: 40,
  marginTop: 16,
  padding: 24,
  width: "100%",
  boxSizing: "border-box",
};

const innerWrap: React.CSSProperties = {
  maxWidth: 860,
  margin: "0 auto",
};

const headerRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 16,
};

const rowCard: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: 12,
  background: "#020617",
  borderRadius: 12,
  border: "1px solid #1e293b",
  cursor: "pointer",
};

const avatarStyle: React.CSSProperties = {
  width: 42,
  height: 42,
  borderRadius: 8,
  backgroundColor: "rgb(38, 29, 162)", // same blue as elsewhere
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 22,
  color: "#fff",
  flexShrink: 0,
};

const pill: React.CSSProperties = {
  fontSize: 12,
  padding: "2px 8px",
  borderRadius: 999,
  border: "1px solid",
  whiteSpace: "nowrap",
};

const btn: React.CSSProperties = {
  padding: "8px 12px",
  borderRadius: 6,
  border: "1px solid #1f2937",
  background: "#020617",
  color: "#e5e7eb",
  cursor: "pointer",
};

const dangerBtn: React.CSSProperties = {
  ...btn,
  background: "#7f1d1d",
  borderColor: "#b91c1c",
};

export default BlockedList;
